import { useTranslation } from 'react-i18next';
import BoxedTextButton from '@/components/@IntranetLibrary/Button/BoxedTextButton';
import SvgImage from '@/components/Module/SvgImage';
import SwapIcon from '@/public/icons/approval/swap-horizontal.svg';
import CaretIcon from '@/public/icons/editor/caret-right-fill.svg';
import {
  normalizeBrandName,
  statusTagClass,
  type OpenCostBasisMode,
  type OpenCostScenario,
} from '@/utils/approval/openConstructionCost';

/** 레거시 getBrandIcon 키(정규화 브랜드명) → 포탈 공통 SvgImage 브랜드 아이콘 이름 */
const BRAND_SVG_NAME: Record<string, string> = {
  all: '통합',
  mlb: 'MLB',
  mlbkids: 'MLB Kids',
  discovery: 'Discovery',
  supra: 'SUPRA',
  sergiotacchini: 'SERGIO TACCHINI',
  duvetica: 'DUVETICA',
};

/** 레거시와 같은 크기(MLB Kids·세르지오 타키니만 24) */
const BRAND_ICON_24 = new Set(['mlbkids', 'sergiotacchini']);

export const BrandIcon = ({ brand }: { brand: string }) => {
  const key = normalizeBrandName(brand);
  const name = BRAND_SVG_NAME[key];
  if (!name) return <div className="brand-logo default-icon">?</div>;
  return <SvgImage name={name} width={BRAND_ICON_24.has(key) ? 24 : 20} />;
};

export const StatusTag = ({
  id,
  status,
  variant,
  label,
  hidden,
}: {
  id: string;
  status: string;
  variant?: string;
  label?: string;
  hidden?: boolean;
}) => {
  const { t } = useTranslation();
  const text = status ? t(status, { context: 'verdict' }) : '';
  return (
    <span
      className={`status-tag ${variant || statusTagClass(status)}`}
      id={id}
      style={hidden ? { display: 'none' } : undefined}
    >
      {text ? (label ? `${label}: ${text}` : text) : ''}
    </span>
  );
};

export const Scenarios = ({ scenarios }: { scenarios: OpenCostScenario[] }) => (
  <div className="scenario" id="value_roi_scenario">
    {scenarios.map((s, i) => {
      const val = s.value || '-';
      return [
        i > 0 && <span key={`d${i}`} className="scenario-divider" />,
        <div key={s.label} className="scenario-item">
          <span className="scenario-label">{s.label}</span>
          <span
            className={s.highlight ? 'scenario-val val-violet' : 'scenario-val'}
          >
            {s.highlight ? <strong>{val}</strong> : val}
          </span>
        </div>,
      ];
    })}
  </div>
);

/** 영업이익 ↔ 직접이익 기준 전환 — 결재↔협조 전환과 같은 BoxedTextButton + Swap 아이콘 */
export const BasisToggleButton = ({
  basisMode,
  hidden,
  onClick,
}: {
  basisMode: OpenCostBasisMode | null;
  hidden?: boolean;
  onClick?: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <BoxedTextButton
      type="button"
      variant="keyColor"
      className="basis-toggle"
      id="btn_basis_toggle"
      style={hidden ? { display: 'none' } : undefined}
      endIcon={<SwapIcon />}
      onClick={onClick}
    >
      {basisMode === 'direct'
        ? t('영업 이익 기준 보기')
        : t('직접 이익 기준 보기')}
    </BoxedTextButton>
  );
};

/** 비교군 대비 판정 딱지 — 증가 ▲(빨강) / 감소 ▼(초록) */
export const JudgeArrow = ({ up }: { up: boolean }) => (
  <CaretIcon
    className="arrow"
    width={8}
    height={8}
    style={{ transform: `rotate(${up ? -90 : 90}deg)` }}
  />
);
